import { flag } from '../../../config';
import { ROLES, ROLE_ROUTES, ROLE_VARIANTS, getDashboardRoles, headerMenuFlags } from '../../../lib/roles';

// The signed-in account links of the redesigned header. Role logic comes only from
// lib/roles.js, with the variant each surface used before the redesign, so nobody gains
// or loses a dashboard link on either screen size:
//   desktop account menu → ROLE_VARIANTS.HEADER (headerMenuFlags, the old desktop user menu)
//   mobile menu panel    → ROLE_VARIANTS.NAV (getDashboardRoles, the old mobile drawer)
// The differences between the two are documented in src/__tests__/roles.test.js.

const DASHBOARDS = {
  [ROLES.ADMIN]: { key: ROLES.ADMIN, label: 'Master dashboard', to: ROLE_ROUTES[ROLES.ADMIN] },
  [ROLES.BUSINESS]: { key: ROLES.BUSINESS, label: 'Business dashboard', to: ROLE_ROUTES[ROLES.BUSINESS] },
  [ROLES.VENUE]: { key: ROLES.VENUE, label: 'Venue dashboard', to: ROLE_ROUTES[ROLES.VENUE] },
};

/** Dashboard order in both menus: admin, business, venue (as the old desktop menu). */
const ORDER = [ROLES.ADMIN, ROLES.BUSINESS, ROLES.VENUE];

/** Which dashboards a user gets, per surface variant (HEADER or NAV). */
export function dashboardRolesFor(user, variant = ROLE_VARIANTS.HEADER) {
  if (!user) return [];
  if (variant === ROLE_VARIANTS.NAV) {
    const roles = getDashboardRoles(user);
    return ORDER.filter((r) => roles.includes(r));
  }
  const { isAdmin, isAdvertiser, isVenuePartner } = headerMenuFlags(user);
  return ORDER.filter((r) => (r === ROLES.ADMIN ? isAdmin : r === ROLES.BUSINESS ? isAdvertiser : isVenuePartner));
}

/**
 * The account menu in two groups: "Dashboards" (admin → Master dashboard, business →
 * Business dashboard, venue → Venue dashboard; omitted when the user has none), then
 * "My account" (Deal wallet with the active-codes badge, Taps rewards only with REWARDS,
 * My profile). Log out is rendered by the menus themselves (it is a button).
 * Returns [{ key, label, links: [{ key, label, to, badge }] }]; `label` names the list.
 */
export function accountGroups(user, { variant = ROLE_VARIANTS.HEADER, activeClaims = 0 } = {}) {
  const groups = [];
  const dashboards = dashboardRolesFor(user, variant).map((role) => ({ ...DASHBOARDS[role], badge: 0 }));
  if (dashboards.length) groups.push({ key: 'dashboards', label: 'Dashboards', links: dashboards });
  const own = [{ key: 'wallet', label: 'Deal wallet', to: '/deal-wallet', badge: activeClaims > 0 ? activeClaims : 0 }];
  if (flag('REWARDS')) own.push({ key: 'rewards', label: 'Taps rewards', to: '/rewards', badge: 0 });
  own.push({ key: 'profile', label: 'My profile', to: '/profile', badge: 0 });
  groups.push({ key: 'account', label: 'My account', links: own });
  return groups;
}

/** "Hi, Pat" (first name), or "Your account" when the profile has none. */
export function greeting(user) {
  const first = String(user?.first_name || '').trim();
  return first ? `Hi, ${first}` : 'Your account';
}

/** Screen-reader text for the active-codes badge. */
export function activeCodesText(count) {
  return `${count} active ${count === 1 ? 'code' : 'codes'}`;
}
